import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Gauge, ShieldCheck, ShieldAlert, Camera, BookOpen, Copy } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_URL });

const PLATFORM_LABELS = {
  tiktok: 'TikTok',
  instagram_reels: 'Instagram Reels',
  facebook_reels: 'Facebook Reels',
  youtube_shorts: 'YouTube Shorts',
  snapchat: 'Snapchat',
  x_twitter: 'X / Twitter',
};

const scoreColor = (s) => (s >= 85 ? '#059669' : s >= 70 ? '#1D4ED8' : s >= 50 ? '#D97706' : '#DC2626');

const SCORE_DIMS = [
  ['hook_strength', 'Hook'],
  ['curiosity', 'Curiosity'],
  ['emotional_trigger', 'Emotion'],
  ['retention', 'Retention'],
  ['clarity', 'Clarity'],
  ['cta_strength', 'CTA'],
  ['shareability', 'Share'],
  ['comment_potential', 'Comments'],
  ['platform_fit', 'Platform fit'],
];

const copy = (t) => navigator.clipboard.writeText(t).then(
  () => toast.success('Copied'),
  () => toast.error('Copy failed'),
);

export default function SharedScriptPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/api/social-studio/shared/${token}`);
        setData(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Share link not found or revoked.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#475569]">Loading shared brief…</div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center px-6 text-center" data-testid="shared-error">
      <div>
        <ShieldAlert size={48} className="mx-auto text-red-500" aria-hidden="true" />
        <h1 className="text-2xl font-bold text-[#0A0F1C] mt-3">Link unavailable</h1>
        <p className="text-sm text-[#64748B] mt-1">{error}</p>
      </div>
    </div>
  );

  const v = data.variant || {};
  const vs = v.viral_scores || {};
  const cmp = v.compliance || {};
  const coach = v.delivery_coach || {};

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="shared-script-page">
      <header className="bg-[#0A0F1C] text-white py-6 px-6">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Sparkles size={20} className="text-amber-400" aria-hidden="true" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/60">DCA Enterprises · Social Studio</div>
            <h1 className="text-xl font-bold" data-testid="shared-title">{v.title || data.topic}</h1>
          </div>
          <span className="ml-auto text-[10px] uppercase tracking-wider bg-white/10 px-2 py-1 rounded-full" data-testid="shared-platform">
            {PLATFORM_LABELS[data.platform] || data.platform}
          </span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <section className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-4" data-testid="shared-script-body">
          <Block label="HOOK (0-3s)" body={v.hook} color="#DC2626" />
          <Block label="PROBLEM (3-13s)" body={v.problem} color="#D97706" />
          <Block label="SOLUTION (13-50s)" body={v.solution} color="#059669" />
          <Block label="CTA (50-60s)" body={v.cta} color="#1D4ED8" />
          {v.caption && (
            <div className="pt-3 border-t border-[#F1F5F9]">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Caption</div>
                <button type="button" onClick={() => copy(v.caption)} aria-label="Copy caption" className="text-[#64748B] hover:text-[#1D4ED8]">
                  <Copy size={12} aria-hidden="true" />
                </button>
              </div>
              <p className="text-sm text-[#0A0F1C] whitespace-pre-wrap">{v.caption}</p>
            </div>
          )}
          {v.hashtags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-2">
              {v.hashtags.map((h) => <span key={h} className="text-xs px-2 py-0.5 bg-[#EFF6FF] text-[#1D4ED8] rounded-full font-medium">{h}</span>)}
            </div>
          )}
        </section>

        {Object.keys(vs).length > 0 && (
          <section className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-4" data-testid="shared-viral-score">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Gauge size={16} className="text-[#1D4ED8]" aria-hidden="true" />
                <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Viral score</h2>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-black" style={{ color: scoreColor(Number(vs.overall || 0)) }}>{Number(vs.overall || 0)}</span>
                <span className="text-xs font-semibold text-[#94A3B8]">/100</span>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-x-4 gap-y-2">
              {SCORE_DIMS.map(([k, label]) => {
                const val = Number(vs[k] || 0);
                return (
                  <div key={k}>
                    <div className="flex justify-between text-xs mb-0.5">
                      <span className="text-[#475569]">{label}</span>
                      <span className="font-bold" style={{ color: scoreColor(val) }}>{val}</span>
                    </div>
                    <div className="h-1.5 bg-[#F1F5F9] rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${val}%`, backgroundColor: scoreColor(val) }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {Object.keys(coach).length > 0 && (
          <section className="bg-white border border-[#E2E8F0] rounded-2xl p-6 space-y-3" data-testid="shared-delivery-coach">
            <div className="flex items-center gap-2">
              <Camera size={16} className="text-[#1D4ED8]" aria-hidden="true" />
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#0A0F1C]">Delivery brief</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {coach.energy_level && <Field label="Energy" value={coach.energy_level} />}
              {coach.facial_expression && <Field label="Expression" value={coach.facial_expression} />}
              {coach.background && <Field label="Background" value={coach.background} />}
              {coach.opening_frame && <Field label="Opening frame" value={coach.opening_frame} />}
              {coach.ending_frame && <Field label="Ending frame" value={coach.ending_frame} />}
            </div>
            <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-[#F1F5F9]">
              <ListBlock label="Emphasize words" items={coach.emphasize_words} />
              <ListBlock label="Suggested pauses" items={coach.suggested_pauses} />
              <ListBlock label="Zoom cues" items={coach.zoom_cues} />
              <ListBlock label="Cut cues" items={coach.cut_cues} />
              <ListBlock label="B-roll" items={coach.b_roll || v.b_roll_suggestions} />
              <ListBlock label="Props" items={coach.props} />
            </div>
          </section>
        )}

        {Object.keys(cmp).length > 0 && (
          <section className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 space-y-2" data-testid="shared-compliance">
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} className="text-emerald-700" aria-hidden="true" />
              <h2 className="text-sm font-bold uppercase tracking-wider text-emerald-700">Compliance</h2>
              <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-emerald-700">{cmp.risk_level || 'low'} risk · {cmp.category || 'general'}</span>
            </div>
            {cmp.suggested_disclaimer && (
              <p className="text-xs text-[#0A0F1C] bg-white border border-emerald-200 rounded-lg p-2">{cmp.suggested_disclaimer}</p>
            )}
          </section>
        )}

        {/* ===== Lead-gen CTA ===== */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#0A0F1C] via-[#1E2B49] to-[#0A0F1C] text-white rounded-2xl p-6 sm:p-8" data-testid="shared-leadgen-cta">
          <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-[#1D4ED8] opacity-20 blur-3xl" aria-hidden="true" />
          <div className="relative space-y-3">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider bg-white/10 border border-white/20 rounded-full">
              <Sparkles size={11} aria-hidden="true" /> Built by DCA Social Studio
            </div>
            <h2 className="text-2xl sm:text-3xl font-black leading-tight">
              Want briefs like this - for your business?
            </h2>
            <p className="text-sm sm:text-base text-white/80 max-w-2xl">
              David C. Ashley personally builds AI-powered content systems for entrepreneurs ready
              to go from invisible to in-demand. Book a free 20-minute strategy session and walk
              away with your first viral hook + 30-day plan.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <a href="/book-session" data-testid="shared-cta-book"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-[#0A0F1C] text-sm font-bold rounded-lg hover:bg-[#F1F5F9] transition">
                Book a free strategy session →
              </a>
              <a href="/services" data-testid="shared-cta-services"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 border border-white/30 text-white text-sm font-semibold rounded-lg hover:bg-white/20 transition">
                See programs
              </a>
              <a href="/contact" data-testid="shared-cta-contact"
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white/80 hover:text-white transition">
                Message David
              </a>
            </div>
            <div className="grid grid-cols-3 gap-3 pt-4 max-w-xl">
              <div>
                <div className="text-2xl font-black text-white">100M+</div>
                <div className="text-[10px] uppercase tracking-wider text-white/60">Views generated</div>
              </div>
              <div>
                <div className="text-2xl font-black text-white">7</div>
                <div className="text-[10px] uppercase tracking-wider text-white/60">Platforms covered</div>
              </div>
              <div>
                <div className="text-2xl font-black text-white">60s</div>
                <div className="text-[10px] uppercase tracking-wider text-white/60">Per viral script</div>
              </div>
            </div>
          </div>
        </section>

        <footer className="text-center text-xs text-[#94A3B8] py-4">
          Brief by DCA Enterprises · Social Studio · Shared {data.created_at ? new Date(data.created_at).toLocaleDateString() : ''}
          <br />
          <a href="/" className="text-[#1D4ED8] hover:underline">DCAEnterprisesInc.com</a>
        </footer>
      </main>
    </div>
  );
}

function Block({ label, body, color }) {
  if (!body) return null;
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color }}>{label}</div>
      <div className="text-sm text-[#0A0F1C] bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3 leading-relaxed">{body}</div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">{label}</div>
      <div className="text-sm text-[#0A0F1C]">{value}</div>
    </div>
  );
}

function ListBlock({ label, items }) {
  if (!items?.length) return null;
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1">{label}</div>
      <ul className="space-y-0.5">
        {items.map((i, idx) => <li key={idx} className="text-xs text-[#475569] before:content-['•'] before:mr-1.5 before:text-[#94A3B8]">{i}</li>)}
      </ul>
    </div>
  );
}
